import { clearUnitsCache } from './unitsCache';
import { clearUnitDetailCache } from './unitDetailCache';
import { clearDigitalAssetsCache } from './digitalAssetsCache';
import { clearMessageBoardCache } from './messageBoardCache';
import { clearRolesCache } from './rolesCache';
import { clearRoleDefinitionsCache } from './roleDefinitionsCache';

// Lazy imports to avoid circular deps — these are simple Map.clear() calls
let _clearProductsCache: (() => void) | null = null;
let _clearProductSearchCache: (() => void) | null = null;

function getProductsCacheClearer() {
  if (!_clearProductsCache) {
    // Dynamic import at first use
    import('../hooks/useProducts').then(m => { _clearProductsCache = m.clearProductsResponseCache; });
  }
  return _clearProductsCache;
}

function getProductSearchCacheClearer() {
  if (!_clearProductSearchCache) {
    import('../hooks/useProductSearch').then(m => { _clearProductSearchCache = m.clearProductSearchCache; });
  }
  return _clearProductSearchCache;
}

// Pre-warm the lazy imports
import('../hooks/useProducts').then(m => { _clearProductsCache = m.clearProductsResponseCache; });
import('../hooks/useProductSearch').then(m => { _clearProductSearchCache = m.clearProductSearchCache; });

interface CacheEntry {
  clear: () => void;
  label: string;
}

const registry: Record<string, CacheEntry> = {
  'get-units': {
    clear: () => clearUnitsCache(),
    label: 'Units cache',
  },
  'get-unit-by-id': {
    clear: () => clearUnitDetailCache(),
    label: 'Unit detail cache',
  },
  'get-digital-assets': {
    clear: () => clearDigitalAssetsCache(),
    label: 'Digital assets cache',
  },
  'get-message-board': {
    clear: () => clearMessageBoardCache(),
    label: 'Message board cache',
  },
  'get-user-roles': {
    clear: () => clearRolesCache(),
    label: 'Roles cache',
  },
  'get-profile-roles': {
    clear: () => clearRoleDefinitionsCache(),
    label: 'Role definitions cache',
  },
  'search-products': {
    clear: () => {
      _clearProductSearchCache?.();
      _clearProductsCache?.();
    },
    label: 'Product search cache',
  },
  'search-products-by-query': {
    clear: () => {
      _clearProductSearchCache?.();
      _clearProductsCache?.();
    },
    label: 'Product search cache',
  },
  'get-groups': {
    clear: () => {
      _clearProductsCache?.();
    },
    label: 'Groups cache',
  },
  'get-group-detail': {
    clear: () => {
      _clearProductsCache?.();
    },
    label: 'Group detail cache',
  },
};

// React Query key prefixes for endpoints managed by useQuery
const queryKeyMap: Record<string, string[]> = {
  'get-service-campaigns-list': ['service-campaigns-list'],
  'get-service-campaigns': ['service-campaigns'],
  'get-service-campaign-detail': ['service-campaign-detail'],
  'get-orders': ['orders'],
  
  'get-addresses': ['addresses'],
  'get-billing-address': ['billing-address'],
  'get-payment-methods': ['payment-methods'],
  'get-shipping-methods': ['shipping-methods'],
  'get-notifications': ['notifications'],
  'get-content-areas': ['content-areas'],
  'get-feature-flags': ['feature-flags'],
  'get-favorites-lists': ['get-favorites-lists'],
  'get-favorites-list-detail': ['get-favorites-list-detail'],
  'favorites-all-items': ['favorites-all-items'],
};

export function clearCacheForEndpoint(endpointName: string): { cleared: boolean; label: string } {
  const entry = registry[endpointName];
  if (entry) {
    entry.clear();
    return { cleared: true, label: entry.label };
  }
  return { cleared: false, label: endpointName };
}

export function getQueryKeyForEndpoint(endpointName: string): string[] | null {
  return queryKeyMap[endpointName] || null;
}

/** Clear every registered module-level cache and return all React Query keys to invalidate */
export function clearAllCaches(): string[][] {
  Object.values(registry).forEach(entry => entry.clear());
  return Object.values(queryKeyMap);
}

/** Extract the edge function name from a full URL */
export function extractEndpointName(url: string): string {
  try {
    const urlObj = new URL(url);
    // Edge function URLs: .../functions/v1/{function-name}
    const parts = urlObj.pathname.split('/');
    return parts[parts.length - 1] || '';
  } catch {
    const parts = url.split('/');
    return parts[parts.length - 1] || '';
  }
}
